import { useQuery } from '@tanstack/react-query';
import { fetchQualityDistribution } from '../api/backlinks';
import { cn, getQualityGrade, qualityGradeConfig, calculatePBNScore } from '../lib/utils';

/**
 * PortfolioRiskStrip - Horizontal color strip showing every backlink
 * as a tiny segment colored by Link Health Score.
 *
 * green = Strong (>=0.60), amber = Moderate (0.35-0.59),
 * orange = Weak (0.15-0.34), red = Toxic (<0.15), grey = unscored
 */

const gradeColors = {
  strong: 'bg-success',
  moderate: 'bg-warning',
  weak: 'bg-orange-400',
  toxic: 'bg-destructive',
  unscored: 'bg-muted-foreground/20',
};

export default function PortfolioRiskStrip({ projectId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['quality-distribution', projectId],
    queryFn: () => fetchQualityDistribution(projectId),
    enabled: !!projectId,
    staleTime: 60000,
  });

  if (isLoading || !data?.backlinks) return null;

  const backlinks = data.backlinks;
  if (backlinks.length === 0) return null;

  // Classify each backlink
  const counts = { strong: 0, moderate: 0, weak: 0, toxic: 0, unscored: 0 };
  let totalScore = 0;
  let scoredCount = 0;

  const segments = backlinks.map((bl) => {
    const score = bl.quality_score != null ? parseFloat(bl.quality_score) : null;
    const grade = score != null ? getQualityGrade(score) : 'unscored';
    counts[grade]++;
    if (score != null) {
      totalScore += score;
      scoredCount++;
    }
    return grade;
  });

  const avgScore = scoredCount > 0 ? Math.round((totalScore / scoredCount) * 100) / 100 : null;

  return (
    <div className="glow-card-filled p-4 rounded-xl">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-muted-foreground">Portfolio Health</h3>
        {avgScore != null && (
          <span className={cn(
            'text-sm font-semibold',
            qualityGradeConfig[getQualityGrade(avgScore)]?.color || 'text-muted-foreground'
          )}>
            Avg: {avgScore}
          </span>
        )}
      </div>

      {/* Color strip */}
      <div className="flex h-3 rounded-full overflow-hidden gap-[1px] bg-muted/30">
        {segments.map((grade, i) => (
          <div
            key={i}
            className={cn('flex-1 min-w-[2px]', gradeColors[grade])}
            style={{ opacity: grade === 'unscored' ? 0.4 : 1 }}
          />
        ))}
      </div>

      {/* Summary pills */}
      <div className="flex flex-wrap items-center gap-3 mt-3">
        {Object.entries(counts).map(([grade, count]) => {
          if (count === 0) return null;
          const config = grade === 'unscored'
            ? { color: 'text-muted-foreground', label: 'N/A' }
            : qualityGradeConfig[grade];
          return (
            <div key={grade} className="flex items-center gap-1.5 text-xs">
              <div className={cn('w-2.5 h-2.5 rounded-full', gradeColors[grade])} />
              <span className={cn('font-medium', config?.color)}>{count}</span>
              <span className="text-muted-foreground">{config?.label || grade}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
